import { toast } from 'react-toastify';
import axios, { AxiosError, AxiosInstance } from 'axios';

import { hide } from '../store/features/theme';
import { logout } from '../store/features/auth';
import { API_ERROR } from '../resources/api-constants';
import CustomCloseButton from '../components/common/CustomCloseButton';
import { NUMBERS, SIZE_IN_BYTES } from '../resources/number-constants';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../resources/message-constants';
import { clearTokens } from './tokenStorage';
import eventBus from './eventBus';

export const renderCustomCloseButton = () => <CustomCloseButton />;

export const catchError = (err: any) => {
    if (err.response) {
        const { status, data } = err.response;
        throw JSON.stringify({ status, msg: data.detail });
    } else if (err.request) {
        throw JSON.stringify({ status: NUMBERS.FIVE_HUNDRED, msg: 'No response received from the server.' });
    } else {
        throw JSON.stringify({ status: NUMBERS.FIVE_HUNDRED, msg: 'Error occurred while processing the request.' });
    }
};

// Handle API error and display toast message
export const handleError = (
    err: AxiosError | any,
    callback: (error: any) => void,
    options?: { showAuthToast?: boolean }
) => {
    let message = 'An unexpected error occurred.';

    const { response, message: errMessage } = err;
    const errorKey = response?.data?.detail;
    const status = response?.status;
    const isAuthErrorStatus = status === API_ERROR.FOUR_ZERO_ONE || status === API_ERROR.FOUR_ZERO_THREE;
    const shouldShowAuthToast = options?.showAuthToast === true;

    // Auth toasts are handled centrally in axios interceptors.
    // Skip local auth toasts here to avoid duplicate notifications.
    if (isAuthErrorStatus) {
        const authMessage =
            errorKey && errorKey in ERROR_MESSAGES
                ? ERROR_MESSAGES[errorKey as keyof typeof ERROR_MESSAGES]
                : ERROR_MESSAGES.TOKEN_NOT_VALID;

        if (shouldShowAuthToast) {
            toast.error(authMessage, { closeButton: renderCustomCloseButton() });
            toast.clearWaitingQueue();
        }

        if (typeof callback === 'function') {
            return callback(response?.data || { message: authMessage });
        }
        return null;
    }

    if (errorKey && errorKey in ERROR_MESSAGES) {
        message = ERROR_MESSAGES[errorKey as keyof typeof ERROR_MESSAGES];
    } else if (errMessage) {
        message = 'Something went wrong. Please try again later.';
    }

    toast.error(message, { closeButton: renderCustomCloseButton() });
    toast.clearWaitingQueue();

    if (typeof callback === 'function') {
        return callback(response?.data || { message });
    }

    return null;
};

// Set default authentication token for axios instance
export const setDefaultAuthToken = (axiosInstance: AxiosInstance, token: string | null) => {
    if (token) {
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
        delete axiosInstance.defaults.headers.common.Authorization;
    }
};

// Convert array to object with a specified key field
export const arrayToObject = (array: any, keyField = '_id') => array.reduce((obj: any, item: any) => {
    obj[item[keyField]] = item;
    return obj;
}, {});

// Check if the error status code is 401 or 403 (unauthorized)
export const isAuth = (error: AxiosError<any>) => {
    if (((error.response?.status === API_ERROR.FOUR_ZERO_ONE)
        || (error.response?.status === API_ERROR.FOUR_ZERO_THREE))) {
        return true;
    }
    return false;
};

// This function gets an access token from the user's local storage and returns it with the Bearer prefix
export const getAccessToken = (): string | null => {
    const token = getValue('authToken');
    return token ? `Bearer ${token}` : null;
};



// Handle API success and display toast message
export const handleSuccess = (messageKey: keyof typeof SUCCESS_MESSAGES | string) => {
    toast.clearWaitingQueue();
    const message = SUCCESS_MESSAGES[messageKey as keyof typeof SUCCESS_MESSAGES] || messageKey;
    toast.success(message, { closeButton: renderCustomCloseButton() });
};

export const storeValue = (key: any, data: any) => {
    localStorage.setItem(key, data);
};

export const getValue = (key: any) => {
    return localStorage.getItem(key);
};

export const isTokenAvailable = () => {
    return !!localStorage.getItem('authToken');
};

export const clearAllLocalStorage = (): void => {
    // Preserve non-sensitive, preference-type keys that should survive logout
    const keysToPreserve = [
        'cookieConsent',
        'cookieConsentDate',
        'hasSeenDemoTierDisclaimer',
    ];

    const preservedEntries: Record<string, string> = {};

    keysToPreserve.forEach((key) => {
        const value = localStorage.getItem(key);
        if (value !== null) {
            preservedEntries[key] = value;
        }
    });

    localStorage.clear();

    Object.entries(preservedEntries).forEach(([key, value]) => {
        localStorage.setItem(key, value);
    });
};

const runLogoutCleanup = () => {
    try {
        clearAllLocalStorage();
    } catch (error) {
        console.info('Error clearing local storage during logout:', error);
    }

    try {
        clearTokens();
    } catch (error) {
        console.info('Error clearing tokens during logout:', error);
    }

    try {
        setDefaultAuthToken(axios, null);
    } catch (error) {
        console.info('Error resetting default auth token during logout:', error);
    }

    try {
        // Dispatch logout event so components (e.g. CookieConsent) can clean up
        eventBus.dispatch('logout');
    } catch (error) {
        console.info('Error dispatching logout event:', error);
    }
};

export const userLogout = async (navigate: any, dispatch: any) => {
    let logoutError: unknown | null = null;

    try {
        const res = await dispatch(logout({})).unwrap();
        dispatch(hide());
        handleSuccess(res?.detail);
    } catch (error) {
        console.info('Error occurred during logout:', error);
        logoutError = error;
    } finally {
        // Even if logout API call fails, clear local auth state.
        runLogoutCleanup();
        try {
            navigate('/');
        } catch (error) {
            console.info('Error navigating after logout:', error);
            if (!logoutError) {
                logoutError = error;
            }
        }
    }

    if (logoutError) {
        throw logoutError;
    }
};

export const removeDuplicateRecords = (arr: any[]) => {
    const uniqueRecords: any[] = [];
    const idSet = new Set<number>(); // Use a Set to keep track of seen IDs

    for (const obj of arr) {
        if (!idSet.has(obj.id)) {
            idSet.add(obj.id);
            uniqueRecords.push(obj);
        }
    }
    return uniqueRecords;
};

const statusMap: any = {
    1: { status: 'OTP Not Verified', class: 'otp-not-verified' },
    2: { status: 'Profile Incomplete', class: 'profile-incomplete' },
    3: { status: 'pending', class: 'pending' },
    4: { status: 'rejected', class: 'inactive' },
    5: { status: 'active', class: 'active' },
    6: { status: 'inactive', class: 'inactive' },
};

export const getStatusObject = (value: any) => {
    return statusMap[value] || { status: 'unknown', class: 'unknown' };
};

// Helper function to format phone numbers without the country code
export const formatPhoneNumber = (phoneNumber: any) => {
    // Remove the country code and any non-numeric characters except for digits
    const digits = phoneNumber.replace(/^\+\d+\s*/, '').replace(/\D/g, '');

    // If the length of the digits is less than 10, add leading zeros to make it up to 10 digits
    const normalizedDigits = digits.padStart(NUMBERS.TEN, '0');

    // Ensure we have exactly 10 digits for formatting, otherwise return the cleaned input as is
    if (normalizedDigits.length < NUMBERS.TEN) {
        return phoneNumber;
    }

    // Extract and format the last 10 digits
    return `(${normalizedDigits.slice(0, NUMBERS.THREE)}) ${normalizedDigits.slice(NUMBERS.THREE, NUMBERS.SIX)}-${normalizedDigits.slice(NUMBERS.SIX, NUMBERS.TEN)}`;
};

// Remove characters from the beginning and end of a string that match a regular expression
export const trimStr = (str: string, regex: RegExp) => {
    if (!str) {
        return str;
    }
    return str.replace(regex, '');
};

export const bytesToKB = (bytes: number, decimals = NUMBERS.TWO): string => {
    if (bytes === 0) {
        return '0 KB';
    }

    const kilobytes = bytes / SIZE_IN_BYTES;
    return `${kilobytes.toFixed(decimals)} KB`;
};

export const removeNullValues = (obj: any) =>
    Object.entries(obj).reduce((acc: any, [key, value]) => {
        if (value !== null) {
            acc[key] = value;
        }
        return acc;
    }, {});

export const removeBlankKeys = (payload: any) => {
    return Object.fromEntries(
        Object.entries(payload).filter(
            ([, value]) => value !== "" && value !== null && value !== undefined
        )
    );
};

export const formatMessage = (str: string) => {
    // Safety check: return empty string if str is undefined or null
    if (!str) {
        return '';
    }

    const keywords: { [key: string]: string } = {
        'employment_type': 'Employment Type',
        'work_mode': 'Work Mode',
        'job_title': 'Job Title',
        'time_zone': 'Timezone',
        'hybrid_schedule': 'Hybrid Schedule',
        'location': 'full address.',
    };

    return str.replace(/\b(employment_type|work_mode|job_title|time_zone|hybrid_schedule|location)\b/g, (match: keyof typeof keywords) => keywords[match]);
};

/**
 * Parses bullet points from text and returns structured data
 * Supports bullet characters: •, -, *
 * @param text - The text to parse
 * @returns Object with introText, closingText, and bulletItems array, or null if no bullets found
 */
export interface ParsedBulletPoints {
    introText: string;
    closingText: string;
    bulletItems: string[];
}

export const parseBulletPoints = (text: string): ParsedBulletPoints | null => {
    if (!text || typeof text !== 'string') {
        return null;
    }

    // Skip parsing for success/notification messages that shouldn't be formatted as bullet lists
    // Check first line for skip patterns (for multiline messages like "Added X suggestion(s) to Y\n• Z")
    const firstLine = text.split(/\n/)[0]?.trim() || '';
    const skipPatterns = [
        /^Added\s+\d+\s+suggestion/i,
        /^Successfully\s+added/i,
        /Added.*suggestion\(s\)/i
    ];

    // Check if text or first line matches any skip pattern
    const shouldSkip = skipPatterns.some(pattern => pattern.test(text) || pattern.test(firstLine));
    if (shouldSkip) {
        return null;
    }

    // Check if text contains bullet points (only at start of line with space after)
    // Make pattern stricter: bullet must be at line start followed by space
    const bulletPattern = /^[•*-]\s/m;
    if (!bulletPattern.test(text)) {
        return null;
    }

    const bulletItems: string[] = [];
    let introText = '';
    let closingText = '';

    // Split by newlines and filter empty lines
    const lines = text.split(/\n/).filter(line => line.trim());
    const isMultiLine = lines.length > 1;

    if (isMultiLine) {
        // Process multiline text
        let foundBullets = false;

        for (const line of lines) {
            const trimmedLine = line.trim();
            const isBulletLine = /^[•*-]\s*/.test(trimmedLine);

            if (isBulletLine) {
                foundBullets = true;
                const item = trimmedLine.replace(/^[•*-]\s*/, '').trim();
                if (item) {
                    bulletItems.push(item);
                }
            } else if (!foundBullets) {
                // Collect intro text before bullets
                introText = introText ? `${introText} ${trimmedLine}` : trimmedLine;
            } else {
                // Collect closing text after bullets
                closingText = closingText ? `${closingText} ${trimmedLine}` : trimmedLine;
            }
        }
    } else {
        // Process single line text
        // Only match bullets at word boundaries (start of line or after space/newline)
        const bulletRegex = /(?:^|\s)([•*-])\s+([^\s]+(?:\s+[^\s]+)*?)(?=\s+[•*-]|$)/g;
        const matches = Array.from(text.matchAll(bulletRegex));

        if (matches.length === 0) {
            return null;
        }

        // Extract intro text (everything before first bullet)
        // Use stricter pattern: bullet at start of line or after space
        const firstBulletMatch = text.match(/(?:^|\s)([•*-])\s+/);
        if (firstBulletMatch && firstBulletMatch.index !== undefined && firstBulletMatch.index > 0) {
            introText = text.substring(0, firstBulletMatch.index).trim();
        } else if (firstBulletMatch && firstBulletMatch.index === 0) {
            // Bullet at start, no intro text
            introText = '';
        }

        // Extract bullet items (match[2] is the text after bullet, match[1] is the bullet char)
        matches.forEach(match => {
            const item = match[2]?.trim();
            if (item) {
                bulletItems.push(item);
            }
        });

        // Extract closing text (everything after last bullet)
        const lastMatch = matches[matches.length - 1];
        if (lastMatch?.index !== undefined) {
            const lastBulletEnd = lastMatch.index + lastMatch[0].length;
            if (lastBulletEnd < text.length) {
                closingText = text.substring(lastBulletEnd).trim();
            }
        }
    }

    // Return null if no bullet items found
    if (bulletItems.length === 0) {
        return null;
    }

    return { introText, closingText, bulletItems };
};

export const generateDetailString = (details: any) => {
    let detailString = 'Finished! These are details based on your provided job info. ';

    for (const [key, value] of Object.entries(details)) {
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

        if (Array.isArray(value)) {
            detailString += `${formattedKey}: ${value.join(', ')}. `;
        } else {
            detailString += `${formattedKey}: ${value}. `;
        }
    }

    return detailString;
};

export const renderErrorToast = (message: any) => {
    toast.error(message, { closeButton: renderCustomCloseButton() });
};

export const extractListItemsFromHTML = (htmlString: string) => {
    if (!htmlString || !htmlString.trim()) return [];
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const listItems = Array.from(doc.querySelectorAll('li'));
    
    // If we found <li> tags, return them
    if (listItems.length > 0) {
        return listItems.map((li) => li.textContent?.trim() || '').filter(Boolean);
    }
    
    // Fallback: If no <li> tags found, check for text directly in <ul> tags
    const ulElements = Array.from(doc.querySelectorAll('ul'));
    if (ulElements.length > 0) {
        return ulElements.map((ul) => ul.textContent?.trim() || '').filter(Boolean);
    }
    
    // Last resort: extract any text content from the parsed document
    const textContent = doc.body.textContent?.trim() || '';
    return textContent ? [textContent] : [];
};

export const extractMinMax = (str: string) => {
    // 1. Handle blank/null/undefined strings
    if (!str || !str.trim()) return { min: 0, max: 0 };

    // 2. Check for "miles" in the string
    if (!str.toLowerCase().includes('miles')) return { min: 0, max: 0 };

    // Extract <li> contents
    const match = str.match(/<li>(.*?)<\/li>/i);
    if (!match) return { min: 0, max: 0 };

    // Remove "miles" and trim
    const text = match[1].replace(/ miles/i, '').trim();

    // Handle range "0-150"
    if (text.includes('-')) {
        const [min, max] = text.split('-').map(Number);
        return { min: isNaN(min) ? 0 : min, max: isNaN(max) ? 0 : max };
    }

    // Handle single value "150"
    const num = parseInt(text, 10);
    return { min: 0, max: isNaN(num) ? 0 : num };
};

export const getCopyrightText = () => {
    const year = new Date().getFullYear();
    return `Copyright ${year}. SBR2TH Inc. All rights reserved.`;
};

export const isValidImageUrl = (url: string) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;

        // Optional timeout
        setTimeout(() => resolve(false), 5000);
    });
}

export const ensureListHTML = (htmlContent = "") => {
    if (typeof htmlContent !== "string") {
        return htmlContent;
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent || "";

    // Check if content already has a list
    const hasList = tempDiv.querySelector("ul, ol");

    if (hasList) {
        return htmlContent; // Return original if a list exists
    }

    // Wrap each line or block in a <li> inside a <ul>
    const lines = htmlContent
        .split(/[\r\n]+/)
        .map((line) => line.trim())
        .filter((line) => line !== "");

    const listItems = lines.map((line) => `<li>${line}</li>`).join("");
    return `<ul>${listItems}</ul>`;
};

export const wrapAllContentInList = (html = "") => {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    const items: any = [];

    // Extract text from <p> tags
    temp.querySelectorAll("p").forEach((p: any) => {
        const text = p?.textContent?.trim();
        if (text) items.push(text);
    });

    // Extract text from existing <li> tags
    temp.querySelectorAll("li").forEach((li: any) => {
        const text = li?.textContent?.trim();
        if (text) items.push(text);
    });

    // Generate a flat <ul> with all items
    const listItems = items.map((text: any) => `<li>${text}</li>`).join("");
    return `<ul>${listItems}</ul>`;
};
